import { useState, useEffect } from "react";
import axios from "axios";
import { Crosshair, RefreshCw, TrendingUp, TrendingDown, AlertTriangle, X, Search, Pin, CalendarClock, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BUCKETS = ["1-5", "6-10", "11-20", "21-50", "51-100", "101-200", "201-365"];

const labelCls = (l) =>
  l === "A setup" ? "bg-emerald-900/60 text-emerald-300 border-emerald-700"
  : l === "B setup" ? "bg-cyan-900/60 text-cyan-300 border-cyan-700"
  : l === "Watchlist" ? "bg-amber-900/60 text-amber-300 border-amber-700"
  : "bg-rose-900/60 text-rose-300 border-rose-700";

const COLS = ["#", "Ticker", "Label", "Combined", "Underlying", "Contract", "Price", "1D", "Regime", "Expiry", "DTE", "Strike", "Δ", "Bid/Ask", "Src", "Spr%", "Bid sz", "Vol", "OI", "V/OI", "IV", "Θ%/day", "Exp move", "R/R", "Warnings"];

const labelTitle = (l) =>
  `${l}: ${l === "A setup" ? "85-100 — strong alignment of direction, liquidity and contract structure" : l === "B setup" ? "70-84 — tradable, but one or two criteria are weaker" : l === "Watchlist" ? "55-69 — needs confirmation or a better entry first" : "below 55 — weak thesis, poor liquidity or expensive pricing"}`;

const SRC = {
  nasdaq: { label: "Nasdaq", cls: "bg-emerald-900/50 text-emerald-200 border-emerald-700", title: "Option chain from Nasdaq.com — first fallback when MSN Finance has no usable chain (IV implied from the mid; bid sizes from Cboe)" },
  yahoo: { label: "Yahoo", cls: "bg-violet-900/50 text-violet-200 border-violet-700", title: "Option chain from Yahoo Finance — used when MSN and Nasdaq.com are unavailable or rate-limited" },
  msn:   { label: "MSN",   cls: "bg-sky-900/50 text-sky-200 border-sky-700",       title: "Option chain from MSN Finance — PRIMARY source (bid/ask/last/volume/OI with no rate limits; IV implied from the mid; bid sizes from Cboe)" },
  cboe:  { label: "Cboe",  cls: "bg-teal-900/50 text-teal-200 border-teal-700",     title: "Option chain from Cboe delayed quotes (exchange Greeks) — last-resort fallback" },
};
const SourceChip = ({ source, quoteTime, testId }) => {
  const m = SRC[source] || SRC.yahoo;
  const qt = quoteTime ? (isNaN(Date.parse(quoteTime)) ? quoteTime : new Date(quoteTime).toLocaleString()) : null;
  return (
    <span data-testid={testId} title={`${m.title}${qt ? ` · quote time ${qt}` : ""}`}
      className={`inline-block px-1 py-0.5 rounded border text-[8px] font-bold uppercase tracking-wide ${m.cls}`}>{m.label}</span>
  );
};

const SideChip = ({ side }) => (
  <span className={`px-1.5 py-0.5 rounded border text-[9px] font-bold ${side === "call" ? "bg-emerald-900/60 text-emerald-300 border-emerald-700" : "bg-rose-900/60 text-rose-300 border-rose-700"}`}>
    {side === "call" ? "CALL" : "PUT"}
  </span>
);

const DOT = {
  green: { cls: "bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.9)]", label: "Bargain fill" },
  yellow: { cls: "bg-amber-300 shadow-[0_0_6px_rgba(252,211,77,0.8)]", label: "Fair fill" },
  red: { cls: "bg-rose-500 shadow-[0_0_6px_rgba(244,63,94,0.9)]", label: "Immediate loss" },
};
const fmtEdge = (v) => `${v >= 0 ? "+" : "−"}$${Math.abs(v).toFixed(2)}`;

const FillDot = ({ fe, testId }) => {
  if (!fe) return <span data-testid={testId} data-color="none" className="inline-block w-2 h-2 rounded-full bg-slate-600 mr-1.5 align-middle" title="Fill edge unavailable (no fair-value reference for this contract)" />;
  const d = DOT[fe.color];
  const title = `${d.label}: buying at the ask ($${fe.ask.toFixed(2)}) vs Black-Scholes fair value $${fe.theoretical.toFixed(2)} = ${fmtEdge(fe.edge_usd)} per contract the moment you're filled (tolerance ±$${fe.tolerance_usd.toFixed(2)}; fair value from ${fe.basis} ${fe.ref_iv}%)`;
  const txt = fe.color === "green" ? "text-emerald-300" : fe.color === "red" ? "text-rose-300" : "text-amber-200";
  return (
    <span className="inline-flex items-center gap-1 mr-1.5 align-middle" title={title}>
      <span data-testid={testId} data-color={fe.color} className={`inline-block w-2 h-2 rounded-full ${d.cls}`} />
      <span data-testid={`${testId}-edge`} className={`text-[9px] font-bold tabular-nums ${txt}`}>{fmtEdge(fe.edge_usd)}</span>
    </span>
  );
};

function ResultRow({ r, onClick, testId, side, relaxed }) {
  const c = r.best_contract;
  return (
    <tr className={`border-b border-slate-800/60 hover:bg-slate-800/40 cursor-pointer ${r.setup_label === "Avoid" ? "opacity-50" : ""}`}
        data-testid={testId} onClick={onClick}>
      <td className="px-2 py-1.5">
        {side ? <div className="flex flex-col gap-0.5 items-start"><SideChip side={side} />{relaxed && <span className="text-[8px] text-amber-300" data-testid={`scanner-lookup-relaxed-${side}`}>relaxed</span>}</div> : r.rank}
        {r.pinned && !side && <Pin className="w-2.5 h-2.5 text-purple-400 inline ml-1" title="pinned" />}
      </td>
      <td className="px-2 py-1.5 font-semibold text-white"><FillDot fe={c.fill_edge} testId={`${testId}-fill-dot`} />{r.ticker}<div className="text-[9px] text-gray-500">{r.name}</div></td>
      <td className="px-2 py-1.5" title={labelTitle(r.setup_label)}><span className={`px-1.5 py-0.5 rounded border text-[9px] font-bold ${labelCls(r.setup_label)}`}>{r.setup_label}</span></td>
      <td className="px-2 py-1.5 font-bold text-purple-300" title="Combined score = 60% underlying setup quality + 40% contract quality (0-100). Tap the row for the full breakdown.">{r.combined_score}</td>
      <td className="px-2 py-1.5">{r.underlying_score}</td>
      <td className="px-2 py-1.5">{r.contract_score}</td>
      <td className="px-2 py-1.5" data-testid={`${testId}-price`}
          title={r.price_time ? `Live quote (${r.price_source}) at ${new Date(r.price_time).toLocaleTimeString()} — refreshed at scan time, never the cached history close` : "Last history close (live quote unavailable)"}>
        ${r.last_price}{r.price_time && <span className="ml-1 inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 align-middle" />}
      </td>
      <td className={`px-2 py-1.5 ${r.return_1d >= 0 ? "text-green-400" : "text-red-400"}`}>{r.return_1d > 0 ? "+" : ""}{r.return_1d}%</td>
      <td className="px-2 py-1.5 text-gray-400">{r.trend_regime}</td>
      <td className="px-1.5 py-1.5"><span className="inline-block px-1.5 py-0.5 rounded bg-amber-500/15 border border-amber-500/50 text-amber-200 font-bold whitespace-nowrap" data-testid={`${testId}-expiry`}>{c.expiration}</span></td>
      <td className="px-2 py-1.5">{c.dte}</td>
      <td className="px-1.5 py-1.5"><span className="inline-block px-1.5 py-0.5 rounded bg-amber-500/15 border border-amber-500/50 text-amber-200 font-bold whitespace-nowrap" data-testid={`${testId}-strike`}>${c.strike}</span></td>
      <td className="px-2 py-1.5">{c.delta}</td>
      <td className="px-2 py-1.5">{c.bid.toFixed(2)}/{c.ask.toFixed(2)}</td>
      <td className="px-1.5 py-1.5"><SourceChip source={c.chain_source} quoteTime={c.quote_time} testId={`${testId}-source`} /></td>
      <td className={`px-2 py-1.5 ${c.spread_pct > 3 ? "text-amber-300" : ""}`}>{c.spread_pct}%</td>
      <td className={`px-2 py-1.5 ${c.bid_size != null && c.bid_size < 10 ? "text-amber-300" : ""}`} title="Bid size (contracts at the best bid) — Liquidity Guard needs ≥10">{c.bid_size ?? "—"}</td>
      <td className="px-2 py-1.5">{c.volume.toLocaleString()}</td>
      <td className="px-2 py-1.5">{c.open_interest.toLocaleString()}</td>
      <td className={`px-2 py-1.5 ${c.vol_oi_ratio != null && c.vol_oi_ratio <= 0.05 ? "text-amber-300" : ""}`} title="Today's volume ÷ open interest — Liquidity Guard needs >0.05">{c.vol_oi_ratio ?? "—"}</td>
      <td className="px-2 py-1.5">{c.iv}%</td>
      <td className="px-2 py-1.5">{c.theta_pct_of_premium}%</td>
      <td className="px-2 py-1.5">±${c.expected_move}</td>
      <td className="px-2 py-1.5">{c.risk_reward ?? "—"}</td>
      <td className="px-2 py-1.5">
        {r.warnings.map(w => (
          <span key={w} className="inline-flex items-center gap-0.5 mr-1 px-1 py-0.5 rounded bg-amber-950/60 border border-amber-800 text-[8px] text-amber-300"><AlertTriangle className="w-2 h-2" />{w}</span>
        ))}
      </td>
    </tr>
  );
}

const TableHead = () => (
  <thead>
    <tr className="text-gray-500 border-b border-slate-800 bg-slate-900/60">
      {COLS.map(h => <th key={h} className={`px-2 py-1.5 text-left font-medium whitespace-nowrap ${h === "Expiry" || h === "Strike" ? "text-amber-300 font-bold" : ""}`}>{h}</th>)}
    </tr>
  </thead>
);

export default function OptionsScannerSection() {
  const [expanded, setExpanded] = useState(false);
  const [und, setUnd] = useState(null);
  const [scan, setScan] = useState(null);
  const [direction, setDirection] = useState("call");
  const [bucket, setBucket] = useState("11-20");
  const [group, setGroup] = useState("all");
  const [guard, setGuard] = useState(true);
  const [dot, setDot] = useState("all");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [detail, setDetail] = useState(null);
  const [showLegend, setShowLegend] = useState(false);
  const [logBusy, setLogBusy] = useState(false);
  const [logged, setLogged] = useState(false);
  const [lookupInput, setLookupInput] = useState("");
  const [lookupTicker, setLookupTicker] = useState("");
  const [lookup, setLookup] = useState(null);
  const [diag, setDiag] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const runDiag = () => {
    setDiagLoading(true);
    axios.get(`${API}/scanner-options/diagnostics`, { timeout: 60000 })
      .then(r => setDiag(r.data)).catch(e => setDiag({ probes: { request: { ok: false, error: e.message, ms: 0 } } }))
      .finally(() => setDiagLoading(false));
  };
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupErr, setLookupErr] = useState(null);
  const [pins, setPins] = useState([]);
  const [pinBusy, setPinBusy] = useState(false);
  const [pinMsg, setPinMsg] = useState(null);

  useEffect(() => { setLogged(false); }, [detail]);

  const loadPins = () => axios.get(`${API}/scanner-options/pins`).then(r => setPins(r.data.pins || [])).catch(() => {});

  const runLookup = (t) => {
    const sym = (t || "").trim().toUpperCase();
    if (!sym) return;
    setLookupTicker(sym); setLookupLoading(true); setLookupErr(null); setLookup(null); setPinMsg(null);
    axios.get(`${API}/scanner-options/lookup`, { params: { ticker: sym, bucket, guard }, timeout: 120000 })
      .then(r => setLookup(r.data))
      .catch(e => setLookupErr(e?.response?.data?.detail || e.message))
      .finally(() => setLookupLoading(false));
  };

  useEffect(() => {
    if (expanded && lookupTicker) runLookup(lookupTicker);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bucket, guard]);

  const clearLookup = () => { setLookupInput(""); setLookupTicker(""); setLookup(null); setLookupErr(null); setPinMsg(null); };

  const pinTicker = async (t) => {
    setPinBusy(true); setPinMsg(null);
    try {
      const r = await axios.post(`${API}/scanner-options/pins`, { ticker: t }, { timeout: 120000 });
      if (r.data.already_in_universe) setPinMsg(`${t} is already part of the ranked universe — it competes in every scan.`);
      else if (r.data.already_pinned) setPinMsg(`${t} is already pinned.`);
      else { setPinMsg(`${t} pinned — it now competes in every scan.`); setLookup(l => l ? { ...l, underlying: { ...l.underlying, pinned: true } } : l); loadPins(); loadUnd(); loadScan(true); }
    } catch (e) { setPinMsg(e?.response?.data?.detail || e.message); }
    finally { setPinBusy(false); }
  };

  const unpin = async (t) => {
    try { await axios.delete(`${API}/scanner-options/pins/${t}`); loadPins(); loadUnd(); loadScan(true); }
    catch (e) { alert(e?.response?.data?.detail || e.message); }
  };

  const loadUnd = (force = false) => {
    axios.get(`${API}/scanner-options/underlyings`, { params: force ? { force: true } : {}, timeout: 120000 })
      .then(r => setUnd(r.data)).catch(() => {});
  };

  const loadScan = (force = false) => {
    setLoading(true); setErr(null); setScan(null);
    axios.get(`${API}/scanner-options/scan`, { params: { direction, bucket, group, guard, dot, ...(force ? { force: true } : {}) }, timeout: 180000 })
      .then(r => setScan(r.data))
      .catch(e => setErr(e?.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!expanded) return;
    loadUnd();
    loadPins();
  }, [expanded]);

  useEffect(() => {
    if (!expanded) return;
    loadScan();
  }, [expanded, direction, bucket, group, guard, dot]);

  const refresh = () => {
    const p = und?.population;
    const stale = !p?.finished_at || (Date.now() - new Date(p.finished_at).getTime()) > 6 * 3600e3;
    if (p && p.status !== "running" && (stale || p.status === "error")) {
      axios.post(`${API}/scanner-options/population/rebuild`).catch(() => {});
    }
    loadUnd(true); loadScan(true);
  };

  useEffect(() => {
    if (!expanded || und?.population?.status !== "running") return;
    const id = setTimeout(() => loadUnd(true), 15000);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded, und]);

  const lead = und?.leadership;

  if (!expanded) {
    return (
      <div className="border border-purple-900/50 bg-purple-950/10 rounded-lg p-4 mt-4" data-testid="options-scanner-section">
        <button data-testid="options-scanner-expand" onClick={() => setExpanded(true)}
          className="flex items-center gap-2 text-left w-full group">
          <Crosshair className="w-5 h-5 text-purple-400" />
          <div>
            <div className="font-bold text-white group-hover:text-purple-300">Options Scanner</div>
            <div className="text-[10px] text-gray-500">Ranked calls/puts across every optionable US-listed stock & ETF (NYSE, NASDAQ, NYSE American, Arca, Cboe — 10,000+ tickers) plus GICS sector / sub-sector ETFs — tap to run (scans option chains, may take up to a minute)</div>
          </div>
        </button>
      </div>
    );
  }

  return (
    <div className="border border-purple-900/50 bg-purple-950/10 rounded-lg px-3 py-4 mt-4" data-testid="options-scanner-section">
      <div className="flex items-center gap-2 mb-1">
        <Crosshair className="w-5 h-5 text-purple-400" />
        <h1 className="text-xl font-bold">Options Scanner</h1>
        <span className="text-[10px] text-gray-500">ranked decision system · underlying first, contract second · math computed in-app (no AI tokens)</span>
        <button data-testid="options-scanner-refresh" onClick={refresh} disabled={loading}
          className="ml-auto flex items-center gap-1 text-[10px] text-purple-300 hover:text-white border border-purple-800 rounded px-2 py-0.5 disabled:opacity-50">
          <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
        <button data-testid="options-scanner-collapse" onClick={() => setExpanded(false)}
          className="text-[10px] text-gray-500 hover:text-white border border-slate-700 rounded px-2 py-0.5">collapse</button>
      </div>
      <div className="text-[10px] text-rose-300/80 mb-3" data-testid="options-scanner-disclaimer">
        Long options can lose 100% of the premium paid. Max loss is always shown. Not personalized financial advice.
      </div>

      {/* Sector leadership board */}
      {lead && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-4" data-testid="scanner-leadership-board">
          <div className="border border-emerald-900/60 bg-emerald-950/20 rounded p-3">
            <div className="text-[10px] uppercase tracking-wider text-emerald-400 font-bold mb-1 flex items-center gap-1"><TrendingUp className="w-3 h-3" /> Top bullish sectors</div>
            {lead.top_bullish.map(s => (
              <div key={s.ticker} className="flex justify-between text-xs py-0.5" data-testid={`lead-bull-${s.ticker}`}>
                <span>{s.name} <span className="text-gray-500">({s.ticker})</span></span>
                <span className="text-emerald-300 font-semibold">{s.score}</span>
              </div>
            ))}
          </div>
          <div className="border border-rose-900/60 bg-rose-950/20 rounded p-3">
            <div className="text-[10px] uppercase tracking-wider text-rose-400 font-bold mb-1 flex items-center gap-1"><TrendingDown className="w-3 h-3" /> Top bearish / lagging sectors</div>
            {lead.top_bearish.map(s => (
              <div key={s.ticker} className="flex justify-between text-xs py-0.5" data-testid={`lead-bear-${s.ticker}`}>
                <span>{s.name} <span className="text-gray-500">({s.ticker})</span></span>
                <span className="text-rose-300 font-semibold">{s.score}</span>
              </div>
            ))}
          </div>
          <div className="border border-slate-800 bg-slate-900/40 rounded p-3">
            <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-1">Sector dispersion</div>
            <div className="text-2xl font-bold" data-testid="scanner-dispersion">{lead.dispersion}</div>
            <div className="text-xs text-gray-400">{lead.dispersion_label}</div>
            <div className="text-[10px] text-gray-500 mt-1" data-testid="scanner-universe-size">
              {und.universe_size?.toLocaleString()} symbols scored{und.expanding ? " · core expanding to full S&P 500…" : ""}
            </div>
            {und.population && (
              <div className="text-[10px] text-gray-500 mt-0.5" data-testid="scanner-population">
                Population: <span className="text-gray-300 font-semibold">{(und.population.directory_total || 0).toLocaleString()}</span> US-listed tickers
                ({(und.population.stocks_total || 0).toLocaleString()} stocks · {(und.population.etfs_total || 0).toLocaleString()} ETFs — NYSE, NYSE American, NYSE Arca, NASDAQ, Cboe) ·
                {" "}{(und.population.optionable_total || 0).toLocaleString()} with listed options
                {und.population.status === "running" && <span className="text-purple-300"> · scoring {und.population.done?.toLocaleString()} / {und.population.to_score?.toLocaleString()}…</span>}
                {und.population.status === "done" && und.population.finished_at && <span> · population refreshed {new Date(und.population.finished_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</span>}
                {und.population.status === "error" && <span className="text-rose-300"> · population build failed ({und.population.last_error}) — tap Refresh</span>}
                {und.population.status === "idle" && <span className="text-gray-500"> · population loading…</span>}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="flex rounded border border-slate-700 overflow-hidden">
          <button data-testid="scanner-tab-calls" onClick={() => setDirection("call")}
            className={`px-4 py-1.5 text-sm font-semibold ${direction === "call" ? "bg-emerald-800/70 text-emerald-100" : "bg-slate-900 text-gray-400 hover:text-white"}`}>
            Calls
          </button>
          <button data-testid="scanner-tab-puts" onClick={() => setDirection("put")}
            className={`px-4 py-1.5 text-sm font-semibold ${direction === "put" ? "bg-rose-800/70 text-rose-100" : "bg-slate-900 text-gray-400 hover:text-white"}`}>
            Puts
          </button>
        </div>
        <div className="flex items-center gap-1 text-xs text-gray-400">
          DTE bucket:
          <select data-testid="scanner-bucket-select" value={bucket} onChange={e => setBucket(e.target.value)}
            className="bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-sm text-white">
            {BUCKETS.map(b => <option key={b} value={b}>{b} days</option>)}
          </select>
        </div>
        <div className="flex rounded border border-slate-700 overflow-hidden" data-testid="scanner-group-toggle">
          {[["all", "All"], ["etfs", "ETFs"], ["stocks", "Stocks"], ["subsectors", "Sub-Sectors"]].map(([v, l]) => (
            <button key={v} data-testid={`scanner-group-${v}`} onClick={() => setGroup(v)}
              className={`px-3 py-1.5 text-xs font-semibold ${group === v ? "bg-purple-800/70 text-purple-100" : "bg-slate-900 text-gray-400 hover:text-white"}`}>
              {l}
            </button>
          ))}
        </div>
        <button data-testid="scanner-guard-toggle" onClick={() => setGuard(g => !g)}
          title="Liquidity Guard: reject contracts with bid-ask spread >3% of mid, bid size <10 contracts, or volume/open-interest ≤0.05 for the session"
          className={`flex items-center gap-1 px-3 py-1.5 rounded border text-xs font-semibold ${guard ? "border-emerald-700 bg-emerald-900/50 text-emerald-200" : "border-slate-700 bg-slate-900 text-gray-400 hover:text-white"}`}>
          <ShieldCheck className="w-3.5 h-3.5" /> Liquidity Guard {guard ? "ON" : "OFF"}
        </button>
        <div className="flex items-center gap-1 text-xs text-gray-400" title="Fill dot filter: each ticker's row shows its best contract with the chosen dot color (instant gain/loss when buying at the ask); tickers with no such contract are dropped">
          <span className={`inline-block w-2 h-2 rounded-full ${dot === "all" ? "bg-slate-500" : DOT[dot].cls}`} />
          <select data-testid="scanner-dot-select" value={dot} onChange={e => setDot(e.target.value)}
            className={`bg-slate-900 border rounded px-2 py-1.5 text-sm ${dot === "all" ? "border-slate-700 text-white" : dot === "green" ? "border-emerald-700 text-emerald-200" : dot === "yellow" ? "border-amber-600 text-amber-200" : "border-rose-700 text-rose-200"}`}>
            <option value="all">All dots</option>
            <option value="green">Green — bargain fill</option>
            <option value="yellow">Yellow — fair fill</option>
            <option value="red">Red — immediate loss</option>
          </select>
        </div>
        {scan?.chain_sources && !loading && (
          <span className="flex items-center gap-1 text-[10px] text-gray-400" data-testid="scanner-chain-sources" title="Where the option chains in this scan came from">
            chains:
            {Object.entries(scan.chain_sources).filter(([, n]) => n > 0).map(([src, n]) => (
              <span key={src} className="flex items-center gap-0.5"><SourceChip source={src} /> {n}</span>
            ))}
            {scan.nasdaq_rate_limited && <span className="ml-1 px-1.5 py-0.5 rounded border border-amber-700 bg-amber-950/60 text-amber-300 font-semibold" data-testid="scanner-nasdaq-rl">Nasdaq.com rate-limited → Yahoo/MSN</span>}
            {scan.yahoo_rate_limited && <span className="ml-1 px-1.5 py-0.5 rounded border border-amber-700 bg-amber-950/60 text-amber-300 font-semibold" data-testid="scanner-yahoo-rl">Yahoo rate-limited → MSN</span>}
          </span>
        )}
        {loading && <span className="flex items-center gap-1 text-xs text-gray-400" data-testid="scanner-loading"><RefreshCw className="w-3 h-3 animate-spin" /> Scanning chains…</span>}
        <button data-testid="scanner-legend-toggle" onClick={() => setShowLegend(v => !v)}
          className="text-[10px] text-purple-300 border border-purple-800 rounded px-2 py-1 hover:bg-purple-900/40">
          {showLegend ? "Hide" : "What do the scores mean?"}
        </button>
      </div>

      {showLegend && (
        <div className="border border-purple-900/50 bg-slate-900/50 rounded p-3 mb-3 text-xs text-gray-300 space-y-2" data-testid="scanner-legend">
          <div>
            <span className="font-bold text-white">Combined score (0–100)</span> — one number answering "how good is this trade idea overall?" It blends two grades:
            <span className="text-purple-300"> 60% Underlying score</span> (is the ETF itself set up to move your way? — trend vs its 20/50/200-day averages, strength vs SPY, momentum (RSI/MFI/MACD), entry location, volume, plus how fairly its options are priced, how easy they are to trade, and reward vs risk) and
            <span className="text-purple-300"> 40% Contract score</span> (is THIS specific option the right instrument? — ease of buying/selling (liquidity), delta fit for the timeframe, price vs actual volatility, daily time-decay cost, and how the target compares to the move the market expects).
            So a <span className="text-white font-semibold">71.1</span> means: a decent directional setup with a solid contract, but a few criteria are less than ideal — check the warnings and the score chips in the setup card to see which.
          </div>
          <div>
            <span className="font-bold text-white">Labels</span> (from the combined score):
            <span className="ml-1 px-1.5 py-0.5 rounded border text-[9px] font-bold bg-emerald-900/60 text-emerald-300 border-emerald-700">A setup</span> 85–100 — strong alignment of direction, liquidity and contract structure;
            <span className="ml-1 px-1.5 py-0.5 rounded border text-[9px] font-bold bg-cyan-900/60 text-cyan-300 border-cyan-700">B setup</span> 70–84 — tradable, but one or two criteria are weaker (e.g. pricier options or a softer trend);
            <span className="ml-1 px-1.5 py-0.5 rounded border text-[9px] font-bold bg-amber-900/60 text-amber-300 border-amber-700">Watchlist</span> 55–69 — needs confirmation or a better entry first;
            <span className="ml-1 px-1.5 py-0.5 rounded border text-[9px] font-bold bg-rose-900/60 text-rose-300 border-rose-700">Avoid</span> below 55 — weak thesis, poor liquidity or expensive pricing.
          </div>
          <div data-testid="scanner-fill-dot-legend">
            <span className="font-bold text-white">Fill dot (next to the ticker)</span> — what happens to your P&amp;L the instant you buy at the ask, measured against the contract's Black-Scholes fair value (IV interpolated from the neighbouring strikes of the same expiry, so the contract's own quote can't flatter itself).
            <span className="inline-block w-2 h-2 rounded-full bg-emerald-400 mx-1 align-middle" /> <span className="text-emerald-300 font-semibold">Green</span> — bargain purchase: the ask is below fair value by more than 1% of the premium (e.g. pay $1,000, marked at $1,050 on fill);
            <span className="inline-block w-2 h-2 rounded-full bg-amber-300 mx-1 align-middle" /> <span className="text-amber-200 font-semibold">Yellow</span> — fair fill: within ±1% of the premium, no bargain either way;
            <span className="inline-block w-2 h-2 rounded-full bg-rose-500 mx-1 align-middle" /> <span className="text-rose-300 font-semibold">Red</span> — you'd be down more than 1% of the premium the moment you're filled. The number beside the dot is that instant gain (+) or loss (−) in dollars per contract; hover it for the fair value used. The <span className="text-white font-semibold">dot dropdown</span> in the toolbar re-runs the scan keeping only contracts of that color: each ticker's row then shows its best-scoring contract with that dot, and tickers without one are dropped.
          </div>
          <div>
            <span className="font-bold text-white">Chain source (Src column)</span> — <SourceChip source="msn" /> MSN Finance is the primary feed (bid/ask/last/volume/OI with no rate limits; IV implied from the mid, bid sizes from Cboe). Live underlying prices also come from MSN at scan time. If MSN has no usable chain the scanner falls back to <SourceChip source="nasdaq" /> Nasdaq.com, then <SourceChip source="yahoo" /> Yahoo Finance (each skipped for 5 minutes after a 403/429), then <SourceChip source="cboe" /> Cboe delayed quotes as the last resort. Hover a Src chip for the quote time.
          </div>
          <div>
            <span className="font-bold text-white">Liquidity Guard</span> (toggle, ON by default) — before any contract is scored it must pass three hard checks: bid-ask spread ≤ 3% of the midpoint, at least 10 contracts on the bid (bid size, from Cboe quotes), and today's volume ÷ open interest &gt; 0.05 (real trading this session, not just stale open interest). Rejection counts by reason are shown above the results. Outside regular hours bid sizes go to 0 and session volume resets, so the guard will reject nearly everything — toggle it off to research after the close.
          </div>
          <div>
            <span className="font-bold text-white">Earnings warning</span> — for stocks, an amber <span className="px-1 py-0.5 rounded bg-amber-950/60 border border-amber-800 text-[8px] text-amber-300">Earnings MM/DD before expiry</span> chip means the company reports before the contract expires: expect a gap and a post-report IV crush. Warning only — the score is not changed.
          </div>
          <div className="text-[10px] text-gray-500">Tap any row to open the setup card — it lists each component grade so you can see exactly what raised or lowered the score.</div>
        </div>
      )}

      {/* Ticker lookup */}
      <form className="flex flex-wrap items-center gap-2 mb-3" data-testid="scanner-lookup-form"
            onSubmit={e => { e.preventDefault(); runLookup(lookupInput); }}>
        <div className="relative">
          <Search className="w-3.5 h-3.5 text-gray-500 absolute left-2 top-1/2 -translate-y-1/2" />
          <input data-testid="scanner-lookup-input" value={lookupInput} onChange={e => setLookupInput(e.target.value.toUpperCase())}
            placeholder="Look up any ticker (e.g. SMH, PLTR)" maxLength={10}
            className="bg-slate-900 border border-slate-700 rounded pl-7 pr-2 py-1.5 text-sm text-white w-56 placeholder:text-gray-600" />
        </div>
        <button type="submit" data-testid="scanner-lookup-submit" disabled={lookupLoading || !lookupInput.trim()}
          className="px-3 py-1.5 rounded bg-purple-800/70 text-purple-100 text-xs font-bold hover:bg-purple-700 disabled:opacity-50">
          {lookupLoading ? "Searching…" : `Find best call + put · ${bucket}D`}
        </button>
        {lookupTicker && !lookupLoading && (
          <button type="button" data-testid="scanner-lookup-clear" onClick={clearLookup}
            className="text-[10px] text-gray-500 hover:text-white border border-slate-700 rounded px-2 py-1">clear</button>
        )}
        <span className="text-[10px] text-gray-500">Best Call and best Put for any symbol, even if it isn't in the ranked results — follows the DTE bucket above.</span>
      </form>

      {/* Pinned tickers */}
      {pins.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 mb-3 text-[10px]" data-testid="scanner-pins">
          <span className="text-gray-500 flex items-center gap-1"><Pin className="w-3 h-3" /> Pinned to universe:</span>
          {pins.map(p => (
            <span key={p.ticker} data-testid={`scanner-pin-${p.ticker}`} title={`${p.name} · ${p.asset_group}`}
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-purple-800 bg-purple-950/40 text-purple-200 font-semibold">
              {p.ticker}
              <button type="button" data-testid={`scanner-unpin-${p.ticker}`} onClick={() => unpin(p.ticker)} title="Remove from universe"
                className="text-gray-500 hover:text-rose-300"><X className="w-3 h-3" /></button>
            </span>
          ))}
        </div>
      )}

      {lookupTicker && (
        <div className="border border-purple-800/60 bg-purple-950/20 rounded p-2 mb-3" data-testid="scanner-lookup-result">
          <div className="flex flex-wrap items-center gap-2 text-[10px] text-purple-300 font-bold uppercase tracking-wider mb-1">
            <Search className="w-3 h-3" /> Lookup: {lookupTicker}{lookup?.underlying?.name && lookup.underlying.name !== lookupTicker ? ` (${lookup.underlying.name})` : ""} · best call + put · {bucket} DTE
            {lookup?.favored && lookup.data_status === "ok" && (
              <span className={`px-1.5 py-0.5 rounded border normal-case tracking-normal ${lookup.favored === "call" ? "border-emerald-800 bg-emerald-950/60 text-emerald-300" : "border-rose-800 bg-rose-950/60 text-rose-300"}`} data-testid="scanner-lookup-favored">
                favored side: {lookup.favored === "call" ? "Calls" : "Puts"} (bullish {lookup.underlying.bullish_score} vs bearish {lookup.underlying.bearish_score})
              </span>
            )}
            {lookup?.earnings_date && (
              <span className="px-1.5 py-0.5 rounded border border-amber-800 bg-amber-950/60 text-amber-300 normal-case tracking-normal flex items-center gap-1" data-testid="scanner-lookup-earnings">
                <CalendarClock className="w-3 h-3" /> next earnings {lookup.earnings_date}
              </span>
            )}
            {lookup && lookup.underlying && !lookup.in_universe && !lookup.underlying.pinned && (
              <button type="button" data-testid="scanner-pin-btn" disabled={pinBusy} onClick={() => pinTicker(lookupTicker)}
                className="ml-auto flex items-center gap-1 px-2 py-0.5 rounded border border-purple-700 bg-purple-900/50 text-purple-100 normal-case tracking-normal hover:bg-purple-800 disabled:opacity-50">
                <Pin className="w-3 h-3" /> {pinBusy ? "Pinning…" : "Pin to scanner universe"}
              </button>
            )}
            {lookup && (lookup.in_universe || lookup.underlying?.pinned) && (
              <span className="ml-auto text-gray-500 normal-case tracking-normal font-normal" data-testid="scanner-lookup-in-universe">
                {lookup.underlying?.pinned ? "pinned — competes in every scan" : "already in the ranked universe"}
              </span>
            )}
          </div>
          {pinMsg && <div className="text-[10px] text-purple-200 mb-1" data-testid="scanner-pin-msg">{pinMsg}</div>}
          {lookupLoading && <div className="text-xs text-gray-400 flex items-center gap-1" data-testid="scanner-lookup-loading"><RefreshCw className="w-3 h-3 animate-spin" /> Pulling history + call/put chains for {lookupTicker}…</div>}
          {lookupErr && <div className="text-xs text-rose-300" data-testid="scanner-lookup-error">{lookupErr}</div>}
          {lookup && !lookup.underlying && (
            <div className="text-xs text-amber-300" data-testid="scanner-lookup-empty">
              {lookup.data_status === "symbol_not_found" ? `No price history found for ${lookupTicker} — check the symbol.`
                : `${lookupTicker} has under a year of trading history — the setup scoring needs 200+ days.`}
            </div>
          )}
          {lookup?.underlying && (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="scanner-lookup-table">
                <TableHead />
                <tbody>
                  {["call", "put"].map(side => {
                    const s = lookup.sides?.[side];
                    if (!s) return null;
                    if (s.result) return <ResultRow key={side} r={s.result} side={side} relaxed={s.relaxed_filters} testId={`scanner-lookup-row-${side}`} onClick={() => setDetail(s.result)} />;
                    return (
                      <tr key={side} className="border-b border-slate-800/60" data-testid={`scanner-lookup-empty-${side}`}>
                        <td className="px-2 py-1.5"><SideChip side={side} /></td>
                        <td className="px-2 py-1.5 text-amber-300" colSpan={COLS.length - 1}>
                          {s.data_status === "chains_unavailable"
                            ? `No ${side} contracts found in the ${bucket}-day window (no listed options for these expiries, or quote feed unavailable) — try another DTE bucket.`
                            : `${s.chain_summary?.contracts ?? ""} ${side} contracts in this window but none is realistically tradable right now${s.chain_summary ? ` — ${s.chain_summary.with_live_bid} have a live bid, tightest spread ${s.chain_summary.tightest_spread_pct ?? "n/a"}%, total open interest ${s.chain_summary.total_open_interest}` : ""}. Effectively untradeable.`}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className="text-[10px] text-gray-500 mt-1">
                ${lookup.underlying.last_price} · {lookup.underlying.trend_regime} · bullish {lookup.underlying.bullish_score} / bearish {lookup.underlying.bearish_score}
                {Object.values(lookup.sides || {}).some(s => s.relaxed_filters) && <span className="text-amber-300"> · rows marked "relaxed" are below the scanner's liquidity gates — research only</span>}
                {" · "}Tap a row for its setup card.
              </div>
            </div>
          )}
        </div>
      )}

      {err && <div className="text-sm text-rose-300 border border-rose-800 rounded p-3 mb-3" data-testid="scanner-error">{err}</div>}
      {scan?.no_trade && !loading && (
        <div className="text-sm text-amber-300 border border-amber-800 rounded p-3" data-testid="scanner-no-trade">
          {scan.data_status === "universe_expanding"
            ? "The scan universe is still expanding to the full S&P 500 + sub-sector ETFs (takes ~2-3 minutes after a fresh start) — tap Refresh shortly."
            : scan.data_status === "chains_unavailable"
            ? "Options quote feeds are temporarily unavailable (source rate-limit or quotes not yet live for the session) — tap the Calls/Puts tab again in a few minutes."
            : scan.dot && scan.dot !== "all"
            ? `No ${direction === "call" ? "call" : "put"} contracts with a ${scan.dot} dot passed the gates in this bucket (${scan.underlyings_tried} underlyings checked)${scan.dot === "green" ? " — bargain fills (ask below fair value) are rare on liquid names; try a wider DTE bucket, Puts/Calls, or Liquidity Guard OFF" : ""}. Set the dot filter back to "All dots" to see every setup.`
            : `No tradable setups pass the liquidity + scoring gates for this direction/bucket right now${guard ? " (Liquidity Guard is ON — outside regular hours bid sizes drop to 0 and session volume resets, so try again during the session or toggle the guard off to research)" : ""} — standing aside is a position.`}
          {scan.data_status === "chains_unavailable" && (
            <div className="mt-2">
              <button data-testid="scanner-diagnostics-btn" onClick={runDiag} disabled={diagLoading}
                className="px-2 py-1 rounded border border-amber-700 bg-amber-950/40 text-[11px] font-semibold text-amber-200 hover:bg-amber-900/40 disabled:opacity-50">
                {diagLoading ? "Probing feeds…" : "Run feed diagnostics"}
              </button>
              {diag && (
                <div data-testid="scanner-diagnostics" className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-0.5 text-[10px] font-mono text-gray-300">
                  {Object.entries(diag.probes || {}).map(([k, v]) => (
                    <div key={k} data-testid={`scanner-diag-${k}`}>
                      <span className={v.ok ? "text-emerald-400" : "text-rose-400"}>{v.ok ? "OK " : "FAIL "}</span>
                      {k}: {v.ok ? JSON.stringify(v.result) : v.error} <span className="text-gray-600">{v.ms}ms</span>
                    </div>
                  ))}
                  <div className="text-gray-500 md:col-span-2">
                    yahoo breaker {diag.breakers?.yahoo?.open ? "OPEN" : "closed"} · nasdaq chains {diag.breakers?.nasdaq_options_rl ? "rate-limited" : "ok"} · yahoo chains {diag.breakers?.yahoo_options_rl ? "rate-limited" : "ok"} · population {diag.population?.status} {diag.population?.last_error ? `— ${diag.population.last_error}` : ""} · core {diag.universe?.core_size} {diag.universe?.expanding ? "(expanding)" : ""}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Results table */}
      {scan?.results?.length > 0 && (
        <div className="overflow-x-auto border border-slate-800 rounded">
          {scan.pool_size != null && (
            <div className="text-[10px] text-gray-500 px-2 py-1 border-b border-slate-800/60" data-testid="scanner-pool-info">
              Ranked from a pool of {scan.pool_size.toLocaleString()} scored underlyings ({scan.group === "all" ? "all groups" : scan.group}) · option chains checked for the top {scan.underlyings_tried} by {scan.direction === "call" ? "bullish" : "bearish"} score among the {scan.eligible_size?.toLocaleString()} trading ≥ ${Math.round((scan.liquidity_prior_usd || 0) / 1e6)}M/day (thinner names stay scored & searchable via lookup) · {scan.results.length} passed the liquidity gates
              {scan.liquidity_guard?.enabled && scan.liquidity_guard.rejections && (
                <span data-testid="scanner-guard-summary"> · <span className="text-emerald-300">Liquidity Guard</span> rejected {((scan.liquidity_guard.rejections.spread || 0) + (scan.liquidity_guard.rejections.bid_size || 0) + (scan.liquidity_guard.rejections.vol_oi || 0) + (scan.liquidity_guard.rejections.bid_size_unknown || 0)).toLocaleString()} of {scan.liquidity_guard.rejections.considered?.toLocaleString()} quoted contracts (spread &gt;3%: {scan.liquidity_guard.rejections.spread}, bid size &lt;10: {scan.liquidity_guard.rejections.bid_size}, vol/OI ≤0.05: {scan.liquidity_guard.rejections.vol_oi}{scan.liquidity_guard.rejections.bid_size_unknown ? `, no size quote: ${scan.liquidity_guard.rejections.bid_size_unknown}` : ""})</span>
              )}
            </div>
          )}
          <table className="w-full text-xs" data-testid="scanner-results-table">
            <TableHead />
            <tbody>
              {scan.results.map(r => (
                <ResultRow key={r.ticker} r={r} testId={`scanner-row-${r.ticker}`} onClick={() => setDetail(r)} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Setup card modal */}
      {detail && (() => { const dDir = detail.direction || direction; return (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-start justify-center overflow-y-auto p-4" onClick={() => setDetail(null)}>
          <div className="bg-[#101024] border border-purple-900/60 rounded-lg max-w-2xl w-full p-5 mt-8" onClick={e => e.stopPropagation()} data-testid="scanner-setup-card">
            <div className="flex justify-between items-start mb-3">
              <div>
                <div className="text-lg font-bold">{detail.ticker} — {dDir === "call" ? "Bullish Call" : "Bearish Put"} Setup</div>
                <span className={`px-1.5 py-0.5 rounded border text-[10px] font-bold ${labelCls(detail.setup_label)}`}>{detail.setup_label} · combined {detail.combined_score}</span>
              </div>
              <button onClick={() => setDetail(null)} data-testid="scanner-setup-close" className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            {(() => { const c = detail.best_contract; return (
              <div className="space-y-3 text-sm">
                <div className="flex flex-wrap items-center gap-3 border border-amber-500/50 bg-amber-500/10 rounded p-3" data-testid="scanner-card-contract-highlight">
                  <div><div className="text-[9px] uppercase text-amber-300/80 font-bold">Expiration</div><div className="text-xl font-bold text-amber-200">{c.expiration}</div><div className="text-[10px] text-gray-400">{c.dte} days out</div></div>
                  <div className="text-2xl text-amber-500/50">·</div>
                  <div><div className="text-[9px] uppercase text-amber-300/80 font-bold">Strike</div><div className="text-xl font-bold text-amber-200">${c.strike} {dDir}</div><div className="text-[10px] text-gray-400">{c.moneyness_pct > 0 === (dDir === "call") ? "in the money" : "out of the money"} · Δ {c.delta}</div></div>
                  <div className="ml-auto text-right"><div className="text-[9px] uppercase text-gray-500 font-bold">Order ticket</div><div className="text-sm font-semibold text-white">BUY {detail.ticker} {c.expiration} ${c.strike} {dDir.toUpperCase()} @ ~${c.mid} (bid {c.bid.toFixed(2)} / ask {c.ask.toFixed(2)})</div></div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                  {[["Mid price", `$${c.mid}`],
                    ["Delta / Gamma", `${c.delta} / ${c.gamma}`], ["Theta / Vega", `${c.theta} / ${c.vega}`],
                    ["IV", `${c.iv}%`], ["Breakeven", `$${c.breakeven}`],
                    ["Intrinsic / Extrinsic", `$${c.intrinsic} / $${c.extrinsic}`], ["Θ % of premium", `${c.theta_pct_of_premium}%/day`]].map(([k, v]) => (
                    <div key={k} className="bg-slate-900/60 rounded p-2"><div className="text-gray-500 text-[9px] uppercase">{k}</div><div className="font-semibold">{v}</div></div>
                  ))}
                </div>
                <div className="border border-slate-800 rounded p-3 space-y-1 text-xs" data-testid="scanner-trade-plan">
                  <div className="text-[10px] uppercase tracking-wider text-purple-400 font-bold">Trade plan</div>
                  <div><span className="text-gray-500">Entry trigger:</span> {detail.entry_trigger}</div>
                  <div><span className="text-gray-500">Invalidation (underlying):</span> ${c.invalidation}</div>
                  <div><span className="text-gray-500">Target 1 / Target 2:</span> ${c.target_1} / ${c.target_2}</div>
                  <div><span className="text-gray-500">Expected move by expiry:</span> ±${c.expected_move} ({c.expected_move_pct}%)</div>
                  <div><span className="text-gray-500">Underlying risk/reward:</span> {c.risk_reward ?? "—"}</div>
                  {detail.earnings_date && (
                    <div className={c.earnings_before_expiry ? "text-amber-300 font-semibold" : "text-gray-400"} data-testid="scanner-card-earnings">
                      <span className="text-gray-500 font-normal">Next earnings:</span> {detail.earnings_date}
                      {c.earnings_before_expiry ? " — falls BEFORE this expiry: gap risk, IV crush after the report" : " — after this expiry (no earnings gap inside the trade)"}
                    </div>
                  )}
                  <div className="text-rose-300 font-semibold" data-testid="scanner-max-loss">Max loss: ${c.max_loss_per_contract} per contract (100% of premium)</div>
                  <div className="text-gray-300" data-testid="scanner-card-source">
                    <span className="text-gray-500">Chain source:</span> <SourceChip source={c.chain_source} quoteTime={c.quote_time} /> {SRC[c.chain_source]?.title || ""}
                  </div>
                  <div className={c.liquidity_guard ? "text-emerald-300" : "text-gray-400"} data-testid="scanner-card-guard">
                    <ShieldCheck className="w-3 h-3 inline mr-1" />Liquidity Guard {c.liquidity_guard ? "passed" : "not applied"}: spread {c.spread_pct}% of mid · bid size {c.bid_size ?? "n/a"} contracts · vol/OI {c.vol_oi_ratio ?? "n/a"}
                  </div>
                </div>
                <div className="grid grid-cols-3 md:grid-cols-6 gap-1 text-[10px]">
                  {Object.entries(c.scores).map(([k, v]) => (
                    <div key={k} className="bg-slate-900/60 rounded p-1.5 text-center"><div className="text-gray-500">{k.replace(/_/g, " ")}</div><div className={`font-bold ${v >= 70 ? "text-emerald-300" : v >= 50 ? "text-amber-300" : "text-rose-300"}`}>{v}</div></div>
                  ))}
                </div>
                {detail.alternatives?.length > 0 && (
                  <div className="text-xs">
                    <div className="text-[10px] uppercase text-gray-500 mb-1">Alternative strikes</div>
                    {detail.alternatives.map(a => (
                      <div key={`${a.expiration}-${a.strike}`} className="flex gap-3 text-gray-300 py-0.5">
                        <span>{a.expiration} ${a.strike}</span><span>Δ {a.delta}</span><span>{a.bid.toFixed(2)}/{a.ask.toFixed(2)}</span><span>score {a.contract_score}</span>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <button data-testid="scanner-log-trade-btn" disabled={logBusy}
                    onClick={async () => {
                      setLogBusy(true);
                      try {
                        await axios.post(`${API}/scanner-options/log`, {
                          ticker: detail.ticker, direction: dDir, expiration: c.expiration, strike: c.strike,
                          entry_option_price: c.mid, entry_underlying: detail.last_price,
                          combined_score: detail.combined_score, setup_label: detail.setup_label,
                          bucket, group, delta: c.delta, iv: c.iv, spread_pct: c.spread_pct,
                          open_interest: c.open_interest, dte: c.dte, target_1: c.target_1,
                          invalidation: c.invalidation,
                        });
                        setLogged(true);
                      } catch (e) { alert(e?.response?.data?.detail || e.message); }
                      finally { setLogBusy(false); }
                    }}
                    className="px-3 py-1.5 rounded bg-purple-800/70 text-purple-100 text-xs font-bold hover:bg-purple-700 disabled:opacity-50">
                    {logged ? "✓ Logged — view in Options Scanner Log" : logBusy ? "Logging…" : "Log Paper Trade @ mid"}
                  </button>
                </div>
                <div className="text-[9px] text-gray-500">{scan?.disclaimer}</div>
              </div>
            ); })()}
          </div>
        </div>
      ); })()}
    </div>
  );
}
